import { useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { removeBetSlipEvent, updateBetSlipEvent } from 'utils/betslip/betslip';
import { formatEventData } from 'utils/betslip/format-subscribed-data';
import { BETSLIP_EVENT_TEMPLATE } from 'utils/constants/betslip/betslip-event-template';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import {
  CommandNames,
  CommandSource
} from 'utils/constants/swarm/swarm-command-names';
import RidGenerator from 'utils/swarm/rid-generator';
import { BetslipEvents, TBetslipEvent } from 'interfaces/betslip';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { betCommand } from 'services/bet-slip';
import { unsubscribeCommand } from 'services/get-data-level';
import { usePrevious } from 'hooks/usePrevious';
import {
  removeSubscribedEvent,
  updateSubscribedEvent
} from 'store/actions/betslip';
import { getEventIds, getSubscriptions } from 'store/selectors/betslip';

interface IBetslipSubscriptionResponse {
  sport: Record<string, Sport>;
}

export const useBetSlipSubscription = (): null => {
  const dispatch = useDispatch();
  const eventIds = useSelector(getEventIds);
  const subscriptions = useSelector(getSubscriptions);

  const prevEventIds = usePrevious(eventIds);
  const prevSubscriptions = usePrevious(subscriptions);
  const [subscriptionIds, setSubscriptionIds] = useState<{
    [key: string]: string;
  }>({});

  const removeSuspendedSelection = (
    resp: IBetslipSubscriptionResponse,
    eventId: number
  ) => {
    if (SpringConfigs.REMOVE_SUSPENDED_SELECTION) {
      const sport = resp?.sport?.[Object.keys(resp.sport)[0]];
      const region = sport?.region?.[Object.keys(sport.region)[0]];
      const game =
        region?.competition?.[Object.keys(region.competition)[0]]?.game;

      if (!game?.[Object.keys(game || {})[0]]) {
        removeBetSlipEvent(eventId);
      }
    }
  };

  const callback = (eventId: number, resp: IBetslipSubscriptionResponse) => {
    removeSuspendedSelection(resp, eventId);

    let eventFormattedData = formatEventData(resp, eventId);

    if (!eventFormattedData) {
      eventFormattedData = {
        [eventId]: {
          ...BETSLIP_EVENT_TEMPLATE,
          suspendReasons: ['other']
        } as TBetslipEvent
      };
    }

    updateBetSlipEvent(eventFormattedData);
    dispatch(updateSubscribedEvent({ eventId, eventData: eventFormattedData }));
  };

  const updateCallback = (
    eventId: number,
    resp: IBetslipSubscriptionResponse
  ) => {
    removeSuspendedSelection(resp, eventId);

    const updatedEvent = formatEventData(resp, eventId);

    dispatch(updateSubscribedEvent({ eventId, eventData: updatedEvent || {} }));
  };

  const checkForAddedEvents = () => {
    const addedEvents = eventIds.filter(
      eventId => !prevEventIds?.includes(eventId)
    );

    addedEvents.forEach(event => {
      const rid = RidGenerator.gForSubscribe('BETSLIP_EVENT_SUBSCRIPTION_');

      setSubscriptionIds(subscriptionIds => ({
        ...subscriptionIds,
        [event]: rid
      }));

      dispatch(updateSubscribedEvent({ eventId: event, eventData: {} }));

      const currentSubscriptionEntity = subscriptions.find(
        subscription => subscription.eventId === event
      );

      betCommand(
        {
          command: CommandNames.GET,
          params: {
            source: CommandSource.BETTING,
            is_betslip: true,
            subscribe: true,
            what: {
              sport: ['id', 'name'],
              region: ['id', 'name'],
              competition: ['id', 'name'],
              game: [
                [
                  'info',
                  'type',
                  'sport_alias',
                  'is_blocked',
                  'team1_name',
                  'team2_name'
                ]
              ],
              market: [
                'name',
                'is_blocked',
                'express_id',
                'prematch_express_id'
              ],
              event: ['id', 'price', 'base', 'name', 'is_blocked']
            },
            where: {
              event: {
                id: Number(event)
              },
              game: {
                id: Number(currentSubscriptionEntity?.gameId)
              }
            }
          },
          rid
        },
        rid,
        (data: IBetslipSubscriptionResponse) => callback(event, data),
        (data: IBetslipSubscriptionResponse) => updateCallback(event, data),
        null
      );
    });
  };

  const removeEvent = <T extends BetslipEvents | typeof subscriptionIds>(
    removedEvents: number[],
    newEvents: T
  ): T => {
    removedEvents.forEach(event => {
      unsubscribeCommand(subscriptionIds[event]);

      if (subscriptionIds[`${event}base`]) {
        unsubscribeCommand(subscriptionIds[`${event}base`]);
      }

      const baseRid = prevSubscriptions?.find(
        sub => sub.eventId === event
      )?.baseRid;

      if (baseRid) {
        unsubscribeCommand(baseRid);
      }

      delete newEvents[event];
    });

    return newEvents;
  };

  const checkForRemovedEvents = () => {
    const removedEvents = prevEventIds?.filter(
      prevEventId => !eventIds?.includes(prevEventId)
    );

    if (removedEvents?.length) {
      removedEvents.forEach(id => {
        dispatch(removeSubscribedEvent(id));
      });
      setSubscriptionIds(subscriptionIds => {
        subscriptionIds = removeEvent(removedEvents, { ...subscriptionIds });

        return subscriptionIds;
      });
    }
  };

  useLayoutEffect(() => {
    checkForAddedEvents();
    checkForRemovedEvents();
  }, [eventIds.join('-')]);

  return null;
};
